// ============================================================
// useReportHistory.js — 보고서 히스토리 & 버전 관리 훅 (B·I)
// ============================================================
// 역할: 생성된 보고서를 localStorage에 자동 저장하고
//       동일한 시나리오에서 여러 번 생성하면 v1/v2/v3로 버전을 자동 관리합니다.
//
// 비유: 워드 프로그램의 "최근 문서" 목록을 자동으로 만들어주는 기능
// ============================================================

import { useState, useCallback } from "react";

// localStorage에 저장할 키 이름
const STORAGE_KEY = "report_copilot_history";
// 최대 저장 개수 (초과 시 오래된 것부터 삭제)
const MAX_HISTORY = 20;

// ── 헬퍼: localStorage에서 히스토리 읽기 ──
function loadHistory() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

// ── 헬퍼: localStorage에 히스토리 저장 ──
function saveHistory(history) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch {
    // localStorage 용량 초과 시 조용히 실패
    console.warn("히스토리 저장 실패: localStorage 용량 초과");
  }
}

// ── 헬퍼: 버전 번호 결정 ──
// 동일한 시나리오 키가 이미 저장된 경우 버전을 +1
function getNextVersion(history, scenarioKey) {
  const sameScenario = history.filter((h) => h.scenarioKey === scenarioKey);
  return sameScenario.length + 1; // v1, v2, v3 ...
}

// ── 메인 훅 ──
export function useReportHistory() {
  // 컴포넌트 상태에도 반영 (실시간 리렌더링을 위해)
  const [history, setHistory] = useState(() => loadHistory());

  // 보고서 추가 저장
  // 사용법: addReport(reportData, scenarioKey, scenarioDescription, tone)
  const addReport = useCallback((reportData, scenarioKey, scenarioDescription, tone) => {
    setHistory((prev) => {
      const version = getNextVersion(prev, scenarioKey);

      // 새 히스토리 항목 구성
      const newEntry = {
        id: `${scenarioKey}_${Date.now()}`,   // 고유 ID
        scenarioKey,
        scenarioDescription: scenarioDescription || scenarioKey,
        tone: tone || "samsung",
        version,                               // 버전 번호 (I 기능)
        reportTitle: reportData.reportTitle || "제목 없음",
        createdAt: new Date().toISOString(),   // 저장 시각
        reportData,                            // 전체 보고서 데이터 저장
      };

      // 새 항목을 맨 앞에 추가 + 최대 개수 초과 시 뒤에서 잘라냄
      const updated = [newEntry, ...prev].slice(0, MAX_HISTORY);
      saveHistory(updated);
      return updated;
    });
  }, []);

  // 개별 보고서 삭제
  const deleteReport = useCallback((id) => {
    setHistory((prev) => {
      const updated = prev.filter((h) => h.id !== id);
      saveHistory(updated);
      return updated;
    });
  }, []);

  // 전체 히스토리 삭제
  const clearHistory = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setHistory([]);
  }, []);

  return { history, addReport, deleteReport, clearHistory };
}
